import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { trackStoreClick } from "./firebaseAnalytics";

/**
 * Add Up 로드맵 공유 링크 랜딩 — /r/:data
 *
 * 앱이 설치돼 있으면 Universal Links 가 이 URL 을 가로채 앱이 직접 연다.
 * 여기까지 왔다는 건 앱이 없거나(대부분) 데스크톱이라는 뜻이라, 로드맵 내용을
 * 그대로 보여주고 App Store 로 보낸다.
 *
 * ⚠️ 데이터는 링크 안에 전부 들어 있다(서버 없음). 포맷은 iOS 의
 *    AddUp/Models/GoalTemplate.swift 와 짝이다 — 한쪽만 바꾸면 깨진다.
 *      {"v":1,"t":제목,"c":톤,"s":[{"t":단계,"n":수량}]}
 *      JSON → raw DEFLATE → base64url
 */

const APP_STORE_URL = "https://apps.apple.com/app/id6776875043";

/** iOS 의 TodoTone 과 같은 집합. 모르는 값은 work 로 떨어뜨린다(앱과 같은 규칙). */
const TONE_COLORS: Record<string, string> = {
  work: "#4B8DF8",
  personal: "#FFA978",
  health: "#5CCBA5",
  mind: "#9C8CF2",
  urgent: "#FF7B8A",
  study: "#E8B84B",
  etc: "#8E99AE",
};

/** iOS 와 같은 상한 — 한쪽만 느슨하면 "열리는데 못 가져오는" 링크가 생긴다. */
const FORMAT_VERSION = 1;
const MAX_STEPS = 100;
const MAX_ENCODED_LENGTH = 6500;
const TITLE_LIMIT = 30;
const COUNT_MIN = 1;
const COUNT_MAX = 999;

type Step = { title: string; count: number | null };
type Template = { title: string; tone: string; steps: Step[] };

type Locale = "ko" | "en";

const copy: Record<Locale, {
  eyebrow: string;
  lead: string;
  stepsLabel: (n: number) => string;
  openApp: string;
  getApp: string;
  note: string;
  loading: string;
  errorTitle: string;
  errorBody: string;
  backHome: string;
}> = {
  ko: {
    eyebrow: "공유받은 로드맵",
    lead: "Add Up 에서 이 로드맵을 그대로 시작할 수 있어요. 진행 상황은 공유되지 않아요 — 처음부터 시작해요.",
    stepsLabel: (n) => `${n}단계`,
    openApp: "앱에서 열기",
    getApp: "App Store에서 받기",
    note: "iPhone · iPad · Apple Watch · 무료로 시작",
    loading: "로드맵을 여는 중…",
    errorTitle: "링크를 읽을 수 없어요",
    errorBody: "링크가 손상됐거나 더 새로운 버전에서 만든 형식이에요. 보낸 분께 다시 받아보세요.",
    backHome: "Add Up 알아보기",
  },
  en: {
    eyebrow: "A shared roadmap",
    lead: "Open it in Add Up and start from step one. Progress isn't shared — you begin fresh.",
    stepsLabel: (n) => `${n} steps`,
    openApp: "Open in the app",
    getApp: "Get it on the App Store",
    note: "iPhone · iPad · Apple Watch · Free to start",
    loading: "Opening the roadmap…",
    errorTitle: "Couldn't read this link",
    errorBody: "It may be damaged, or made by a newer version. Ask the sender to share it again.",
    backHome: "About Add Up",
  },
};

/** base64url → raw DEFLATE 해제 → JSON. 들어오는 값은 전부 남이 만든 것으로 취급한다. */
async function decodeTemplate(raw: string): Promise<Template> {
  if (!raw || raw.length > MAX_ENCODED_LENGTH) throw new Error("length");

  const b64 = raw.replace(/-/g, "+").replace(/_/g, "/");
  const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);

  let bytes: Uint8Array;
  try {
    const bin = atob(padded);
    bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
  } catch {
    throw new Error("base64");
  }

  // Apple 의 .zlib 은 zlib 헤더가 없는 raw DEFLATE 라 deflate-raw 와 짝이 맞는다.
  const stream = new Blob([bytes as BlobPart]).stream()
    .pipeThrough(new (window as any).DecompressionStream("deflate-raw"));
  const text = await new Response(stream).text();

  const wire = JSON.parse(text);
  if (wire?.v !== FORMAT_VERSION) throw new Error("version");

  const title = sanitize(wire.t);
  const rawSteps = Array.isArray(wire.s) ? wire.s : [];
  if (!title || rawSteps.length === 0 || rawSteps.length > MAX_STEPS) throw new Error("shape");

  const steps: Step[] = rawSteps.map((s: any) => {
    const t = sanitize(s?.t);
    if (!t) throw new Error("step");
    const n = typeof s?.n === "number" && s.n >= COUNT_MIN && s.n <= COUNT_MAX ? s.n : null;
    return { title: t, count: n };
  });

  const tone = typeof wire.c === "string" && TONE_COLORS[wire.c] ? wire.c : "work";
  return { title, tone, steps };
}

/** 제어문자를 걷어내고 길이를 제한한다 — 앱과 같은 규칙(ZWJ 는 이모지라 남긴다). */
function sanitize(value: unknown): string {
  if (typeof value !== "string") return "";
  const cleaned = Array.from(value)
    .filter((ch) => {
      const code = ch.codePointAt(0) ?? 0;
      if (ch === "‍") return true; // 이모지 결합자
      // C0/C1 제어문자 + 양방향 재정의 등 포맷 문자 제거
      if (code < 0x20 || (code >= 0x7f && code <= 0x9f)) return false;
      if (code >= 0x200b && code <= 0x200f) return false;
      if (code >= 0x202a && code <= 0x202e) return false;
      if (code === 0xfeff) return false;
      return true;
    })
    .join("");
  return Array.from(cleaned.trim()).slice(0, TITLE_LIMIT).join("");
}

export default function RoadmapShare() {
  const { data } = useParams<{ data: string }>();
  const [template, setTemplate] = useState<Template | null>(null);
  const [failed, setFailed] = useState(false);

  const locale: Locale = useMemo(
    () => (navigator.language || "en").toLowerCase().startsWith("ko") ? "ko" : "en",
    []
  );
  const t = copy[locale];

  useEffect(() => {
    let alive = true;
    decodeTemplate(data ?? "")
      .then((parsed) => { if (alive) setTemplate(parsed); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [data]);

  const accent = template ? TONE_COLORS[template.tone] : TONE_COLORS.work;

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        {failed ? (
          <>
            <h1 style={styles.errorTitle}>{t.errorTitle}</h1>
            <p style={styles.lead}>{t.errorBody}</p>
            <a href="/addup" style={{ ...styles.secondaryButton, marginTop: 24 }}>
              {t.backHome}
            </a>
          </>
        ) : !template ? (
          <p style={styles.lead}>{t.loading}</p>
        ) : (
          <>
            <span style={{ ...styles.eyebrow, color: accent }}>{t.eyebrow}</span>

            <h1 style={styles.title}>
              <span style={{ ...styles.toneDot, background: accent }} />
              {template.title}
            </h1>

            <p style={styles.stepCount}>{t.stepsLabel(template.steps.length)}</p>

            <ol style={styles.list}>
              {template.steps.map((step, i) => (
                <li key={i} style={styles.item}>
                  <span style={{ ...styles.badge, background: `${accent}22`, color: accent }}>
                    {i + 1}
                  </span>
                  <span style={styles.itemTitle}>{step.title}</span>
                  {step.count !== null && <span style={styles.count}>{step.count}</span>}
                </li>
              ))}
            </ol>

            <p style={styles.lead}>{t.lead}</p>

            <a
              href={`addup://roadmap?d=${encodeURIComponent(data ?? "")}`}
              style={{ ...styles.primaryButton, background: accent }}
            >
              {t.openApp}
            </a>
            <a
              href={APP_STORE_URL}
              onClick={() =>
                trackStoreClick({
                  app_name: "addup",
                  store_name: "app_store",
                  source_page: "roadmap_share",
                })
              }
              style={styles.secondaryButton}
              target="_blank"
              rel="noreferrer"
            >
              {t.getApp}
            </a>
            <p style={styles.note}>{t.note}</p>
          </>
        )}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(180deg, #eef4fe 0%, #ffffff 34%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "40px 20px",
    boxSizing: "border-box",
  },
  card: {
    width: "100%",
    maxWidth: 440,
    background: "#fff",
    borderRadius: 24,
    padding: "32px 26px 28px",
    boxShadow: "0 18px 50px rgba(28, 44, 80, 0.10)",
    textAlign: "center",
  },
  eyebrow: {
    display: "inline-block",
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: "0.04em",
    marginBottom: 10,
  },
  title: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    fontSize: 26,
    lineHeight: 1.25,
    fontWeight: 800,
    color: "#1B2437",
    margin: "0 0 6px",
    wordBreak: "keep-all",
  },
  toneDot: { width: 12, height: 12, borderRadius: "50%", flexShrink: 0 },
  stepCount: { fontSize: 14, color: "#7A869F", margin: "0 0 22px", fontWeight: 600 },
  list: { listStyle: "none", margin: "0 0 24px", padding: 0, textAlign: "left" },
  item: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "11px 14px",
    borderRadius: 14,
    background: "#F6F8FC",
    marginBottom: 8,
  },
  badge: {
    width: 26,
    height: 26,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 800,
    flexShrink: 0,
  },
  itemTitle: { flex: 1, fontSize: 15.5, fontWeight: 600, color: "#1B2437", wordBreak: "keep-all" },
  count: { fontSize: 13, fontWeight: 700, color: "#9AA5BC" },
  lead: { fontSize: 14.5, lineHeight: 1.6, color: "#5A6781", margin: "0 0 20px", wordBreak: "keep-all" },
  primaryButton: {
    display: "block",
    padding: "15px 20px",
    borderRadius: 14,
    color: "#fff",
    fontSize: 16,
    fontWeight: 700,
    textDecoration: "none",
    marginBottom: 10,
  },
  secondaryButton: {
    display: "block",
    padding: "14px 20px",
    borderRadius: 14,
    background: "#EFF3FA",
    color: "#36435E",
    fontSize: 15,
    fontWeight: 700,
    textDecoration: "none",
  },
  errorTitle: { fontSize: 22, fontWeight: 800, color: "#1B2437", margin: "0 0 12px" },
  note: { fontSize: 12.5, color: "#9AA5BC", margin: "16px 0 0" },
};
